// INBOX-PREFS: a per-item override (06 §Notification preferences): on a task's header and a DE's
// page, "Notify: All / Mentions / Mute" — or Default (no override, the person's rows decide).
// Saved in the person's preferences (`notifications.setPrefs` `overrides`); waiting items always
// come through (the server's floor).
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellDotIcon, BellOffIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { notificationKeys, notificationPrefsQuery } from "./notification-queries";

type Level = "all" | "mentions" | "mute";
type Choice = Level | "default";

export const LEVEL_LABELS: Record<Choice, string> = {
  default: "Default",
  all: "All",
  mentions: "Mentions",
  mute: "Mute",
};

export function NotifyLevelMenu({
  api,
  co,
  subjectKind,
  subjectId,
  compact = false,
}: {
  api: Api;
  co: string;
  subjectKind: "task" | "agent";
  subjectId: string;
  /** Icon only (a crowded header); the level is in its accessible name. */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  // Read when the menu opens (or already cached): the header costs no call of its own.
  const [opened, setOpened] = useState(false);
  const prefs = useQuery({ ...notificationPrefsQuery(api, co), enabled: opened });
  const current: Choice =
    prefs.data?.overrides.find((o) => o.subject_kind === subjectKind && o.subject_id === subjectId)
      ?.level ?? "default";
  const save = useMutation({
    mutationFn: (level: Choice) =>
      api.client.notifications.setPrefs({
        params: { co },
        body: {
          overrides: [
            {
              subject_kind: subjectKind,
              subject_id: subjectId,
              level: level === "default" ? null : level,
            },
          ],
        },
      }),
    onSuccess: (data, level) => {
      qc.setQueryData(notificationKeys.prefs(co), data);
      toast.add({ title: `Notify: ${LEVEL_LABELS[level]}` });
    },
    onError: (e) =>
      toast.add({ title: "Not saved", description: e instanceof Error ? e.message : String(e) }),
  });
  const Icon = current === "mute" ? BellOffIcon : BellDotIcon;
  const label = prefs.data ? `Notify: ${LEVEL_LABELS[current]}` : "Notify";
  return (
    <DropdownMenu onOpenChange={(open) => open && setOpened(true)}>
      <DropdownMenuTrigger
        render={
          <Button
            variant={compact ? "ghost" : "secondary"}
            size={compact ? "icon-sm" : "sm"}
            data-slot="notify-level"
            data-level={current}
            aria-label={label}
            title={label}
          />
        }
      >
        <Icon aria-hidden="true" />
        {compact ? null : <span className="max-sm:sr-only">{label}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={prefs.data ? current : undefined}
          onValueChange={(v) => {
            if (prefs.data && v !== current) save.mutate(v as Choice);
          }}
        >
          {(Object.keys(LEVEL_LABELS) as Choice[]).map((k) => (
            <DropdownMenuRadioItem key={k} value={k} closeOnClick>
              {LEVEL_LABELS[k]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
